import { 
  getVideoMetadata, 
  getVideoTranscript, 
  extractVideoId, 
  calculateTimeSaved,
  VideoMetadata,
  VideoTranscript
} from './youtubeService';

import {
  generateCapsuleContent,
  chatWithAI,
  CapsuleContent,
  ChatMessage,
  MindMapNode,
  FAQ,
  MCQ,
  FlashCard,
  TermDefinition
} from './openaiService';

import {
  playTextAudio,
  downloadTextAudio
} from './textToSpeechService';

import { db, storage } from './firebase';
import { 
  collection, 
  addDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

// Enums for language and category options
export enum VideoLanguage {
  English = 'English',
  Hindi = 'Hindi',
  Portuguese = 'Portuguese',
  Marathi = 'Marathi',
  Other = 'Other'
}

export enum OutputLanguage {
  English = 'English',
  Hindi = 'Hindi',
  Hinglish = 'Hinglish',
  Brazilian = 'Brazilian',
  Brazinglish = 'Brazinglish',
  Marathi = 'Marathi',
  Marathinglish = 'Marathinglish'
}

export enum ContentCategory {
  General = 'general',
  Educational = 'educational',
  News = 'news',
  DoglaMeter = 'dogla-meter',
  Sports = 'sports',
  Podcast = 'podcast'
}

// Re-export types from openaiService for component use
export type { MindMapNode, FAQ, MCQ, FlashCard, TermDefinition };

// Extended MindMapNode interface for component use
export interface ExtendedMindMapNode extends MindMapNode {
  label?: string;  // Alias for name
  details?: string;  // Additional details
}

// Additional types needed by components
export interface StudyNotesContent {
  title: string;
  sections: {
    heading: string;
    content: string;
  }[];
  fullNotes?: string;
  summary?: string;
  keyPoints?: string[];
  tables?: {
    title: string;
    headers: string[];
    rows: string[][];
  }[];
}

export interface Terminology {
  term: string;
  definition: string;
  context?: string;
}

export interface CapsuleRequest {
  videoUrl: string;
  outputLanguage: string;
  category: string;
  userId?: string;
}

export interface Capsule {
  id?: string;
  videoId: string;
  videoMetadata: VideoMetadata;
  content: CapsuleContent;
  outputLanguage: string;
  category: string;
  userId?: string;
  createdAt?: any;
  updatedAt?: any;
  timeSaved?: number;
}

/**
 * Creates a capsule from a YouTube video
 * @param request Capsule request parameters
 * @returns Promise with created capsule
 */
export const createCapsule = async (request: CapsuleRequest): Promise<Capsule> => {
  try {
    // Extract video ID from URL
    const videoId = extractVideoId(request.videoUrl);
    
    if (!videoId) {
      throw new Error('Invalid YouTube URL');
    }
    
    // Check if capsule already exists for this video and user
    if (request.userId) {
      const existingCapsule = await getCapsuleByVideoId(videoId, request.userId);
      
      if (existingCapsule) {
        return existingCapsule;
      }
    }
    
    // Get video metadata
    const metadata = await getVideoMetadata(videoId);
    
    // Get video transcript
    let transcript: VideoTranscript;
    
    try {
      // Try to get transcript from YouTube
      transcript = await getVideoTranscript(videoId);
    } catch (error) {
      // If YouTube transcript fails, create a simplified transcript from the video title and description
      console.log('YouTube transcript failed, creating simplified transcript...');
      
      // Create a simplified transcript from the video metadata
      transcript = {
        items: [
          {
            text: `${metadata.title}. ${metadata.description}`,
            start: 0,
            duration: 0,
          }
        ],
        language: 'en',
        isAutoGenerated: false,
      };
      
      // Log the error for debugging
      console.error('Original transcript error:', error);
    }
    
    // Generate capsule content
    const content = await generateCapsuleContent(
      metadata,
      transcript,
      request.outputLanguage,
      request.category
    );
    
    // Calculate time saved
    // Assuming average reading speed of 250 words per minute
    // And estimating word count from fullCapsule
    const wordCount = typeof content.fullCapsule === 'string'
      ? content.fullCapsule.split(/\s+/).length
      : 500; // Default word count if fullCapsule is not a string
    
    // Convert duration string to seconds
    const durationParts = metadata.duration.split(':');
    let durationSeconds = 0;
    
    if (durationParts.length === 3) {
      // Format: H:MM:SS
      durationSeconds = 
        parseInt(durationParts[0]) * 3600 + 
        parseInt(durationParts[1]) * 60 + 
        parseInt(durationParts[2]);
    } else if (durationParts.length === 2) {
      // Format: M:SS
      durationSeconds = 
        parseInt(durationParts[0]) * 60 + 
        parseInt(durationParts[1]);
    }
    
    const timeSaved = calculateTimeSaved(durationSeconds, 250, wordCount);
    
    // Clean up the content to ensure it's properly formatted
    // This is especially important for the fullCapsule field
    if (content && typeof content.fullCapsule === 'string') {
      try {
        // Extract only the numbered points
        const numberedPointsRegex = /\d+\.\s*[^\n]+/g;
        const matches = content.fullCapsule.match(numberedPointsRegex);
        
        if (matches && matches.length > 0) {
          // Filter out empty points (points that are just quotes or empty)
          const validPoints = matches.filter(point => {
            const pointContent = point.replace(/^\d+\.\s*/, '').trim();
            return pointContent !== '' && pointContent !== '"' && pointContent !== '",';
          });
          
          // Join all valid numbered points with newlines
          content.fullCapsule = validPoints.join('\n\n');
          console.log(`Extracted ${validPoints.length} valid numbered points for capsule`);
        }
        
        // Remove any remaining JSON artifacts
        content.fullCapsule = content.fullCapsule
          .replace(/",\s*"keyInsights"[\s\S]*$/, '') // Remove everything after fullCapsule
          .replace(/\\n/g, '\n') // Replace escaped newlines
          .replace(/\\"/g, '"') // Replace escaped quotes
          .replace(/\\\\/g, '\\') // Replace escaped backslashes
          .replace(/\n\d+\.\s*\n/g, '\n') // Remove empty numbered points
          .replace(/\n{3,}/g, '\n\n'); // Replace multiple newlines with double newlines
      } catch (error) {
        console.error('Error cleaning content.fullCapsule:', error);
      }
    }
    
    // Create capsule object
    const capsule: Capsule = {
      videoId,
      videoMetadata: metadata,
      content,
      outputLanguage: request.outputLanguage,
      category: request.category,
      userId: request.userId,
      timeSaved,
    };
    
    // Save capsule to Firestore if user is logged in
    if (request.userId) {
      await saveCapsule(capsule);
    }
    
    return capsule;
  } catch (error) {
    console.error('Error creating capsule:', error);
    throw error;
  }
};

/**
 * Saves a capsule to Firestore
 * @param capsule Capsule to save
 * @returns Promise with saved capsule ID
 */
export const saveCapsule = async (capsule: Capsule): Promise<string> => {
  try {
    if (!capsule.userId) {
      throw new Error('User ID is required to save capsule');
    }
    
    // Add timestamps
    const capsuleWithTimestamps = {
      ...capsule,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'capsules'), capsuleWithTimestamps);
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving capsule:', error);
    throw error;
  }
};

/**
 * Gets a capsule by ID
 * @param id Capsule ID
 * @returns Promise with capsule or null if not found
 */
export const getCapsule = async (id: string): Promise<Capsule | null> => {
  try {
    const docRef = doc(db, 'capsules', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Capsule;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting capsule:', error);
    throw error;
  }
};

/**
 * Gets a capsule by video ID and user ID
 * @param videoId YouTube video ID
 * @param userId User ID
 * @returns Promise with capsule or null if not found
 */
export const getCapsuleByVideoId = async (videoId: string, userId: string): Promise<Capsule | null> => {
  try {
    const q = query(
      collection(db, 'capsules'),
      where('videoId', '==', videoId),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Capsule;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting capsule by video ID:', error);
    throw error;
  }
};

/**
 * Gets all capsules for a user
 * @param userId User ID
 * @returns Promise with array of capsules
 */
export const getUserCapsules = async (userId: string): Promise<Capsule[]> => {
  try {
    const q = query(
      collection(db, 'capsules'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Capsule[];
  } catch (error) {
    console.error('Error getting user capsules:', error);
    throw error;
  }
};

/**
 * Updates a capsule
 * @param id Capsule ID
 * @param updates Partial capsule with updates
 * @returns Promise that resolves when update is complete
 */
export const updateCapsule = async (id: string, updates: Partial<Capsule>): Promise<void> => {
  try {
    const docRef = doc(db, 'capsules', id);
    
    // Add updated timestamp
    const updatesWithTimestamp = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(docRef, updatesWithTimestamp);
  } catch (error) {
    console.error('Error updating capsule:', error);
    throw error;
  }
};

/**
 * Deletes a capsule
 * @param id Capsule ID
 * @returns Promise that resolves when delete is complete
 */
export const deleteCapsule = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'capsules', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting capsule:', error);
    throw error;
  }
};

/**
 * Chats with AI about a capsule
 * @param capsule Capsule to chat about
 * @param messages Previous chat messages
 * @returns Promise with AI response
 */
export const chatWithCapsule = async (
  capsule: Capsule,
  messages: ChatMessage[]
): Promise<string> => {
  try {
    // Get video transcript
    let transcript: VideoTranscript;
    
    try {
      // Try to get transcript from YouTube
      transcript = await getVideoTranscript(capsule.videoId);
    } catch (error) {
      // If YouTube transcript fails, use a simplified transcript from the capsule content
      console.log('YouTube transcript failed, using simplified transcript...');
      
      // Create a simplified transcript from the capsule content
      const transcriptText = typeof capsule.content.fullCapsule === 'string'
        ? capsule.content.fullCapsule
        : JSON.stringify(capsule.content.fullCapsule);
        
      transcript = {
        items: [
          {
            text: transcriptText,
            start: 0,
            duration: 0,
          }
        ],
        language: 'en',
        isAutoGenerated: false,
      };
    }
    
    // Chat with AI
    return await chatWithAI(messages, transcript, capsule.videoMetadata);
  } catch (error) {
    console.error('Error chatting with capsule:', error);
    throw error;
  }
};

/**
 * Plays audio for capsule content
 * @param text Text to play
 * @param language Language of the text
 * @returns Promise that resolves when audio starts playing
 */
export const playCapsuleAudio = async (text: string, language: string): Promise<void> => {
  try {
    return await playTextAudio(text, language);
  } catch (error) {
    console.error('Error playing capsule audio:', error);
    throw error;
  }
};

/**
 * Downloads audio for capsule content
 * @param text Text to download as audio
 * @param filename Filename for the downloaded audio
 * @param language Language of the text
 * @returns Promise that resolves when download is complete
 */
export const downloadCapsuleAudio = async (
  text: string,
  filename: string,
  language: string
): Promise<void> => {
  try {
    return await downloadTextAudio(text, filename, language);
  } catch (error) {
    console.error('Error downloading capsule audio:', error);
    throw error;
  }
};

/**
 * Shares a capsule
 * @param capsule Capsule to share
 * @returns Promise with share URL
 */
export const shareCapsule = async (capsule: Capsule): Promise<string> => {
  try {
    if (!capsule.id) {
      throw new Error('Capsule ID is required to share');
    }
    
    // Create a JSON representation of the capsule
    const capsuleJson = JSON.stringify({
      videoId: capsule.videoId,
      videoMetadata: {
        title: capsule.videoMetadata.title,
        channelTitle: capsule.videoMetadata.channelTitle,
        thumbnailUrl: capsule.videoMetadata.thumbnailUrl,
      },
      content: {
        fullCapsule: typeof capsule.content.fullCapsule === 'string'
          ? capsule.content.fullCapsule.substring(0, 100) + '...'
          : JSON.stringify(capsule.content.fullCapsule).substring(0, 100) + '...',
        keyInsights: capsule.content.keyInsights.slice(0, 3),
      },
      outputLanguage: capsule.outputLanguage,
      category: capsule.category,
      timeSaved: capsule.timeSaved,
    });
    
    // Upload to Firebase Storage
    const storageRef = ref(storage, `shared-capsules/${capsule.id}.json`);
    await uploadString(storageRef, capsuleJson);
    
    // Get download URL
    const url = await getDownloadURL(storageRef);
    
    return url;
  } catch (error) {
    console.error('Error sharing capsule:', error);
    throw error;
  }
};

// Individual content generation functions for backward compatibility
export const generateFullCapsule = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<string> => {
  try {
    const capsule = await createCapsule({
      videoUrl,
      outputLanguage,
      category
    });
    
    // Extract only the fullCapsule field
    let fullCapsule = "";
    
    if (typeof capsule.content.fullCapsule === 'string') {
      fullCapsule = capsule.content.fullCapsule;
      
      // Extract only the numbered points
      try {
        const numberedPointsRegex = /\d+\.\s*[^\n]+/g;
        const matches = fullCapsule.match(numberedPointsRegex);
        
        if (matches && matches.length > 0) {
          // Filter out empty points (points that are just quotes or empty)
          const validPoints = matches.filter(point => {
            const content = point.replace(/^\d+\.\s*/, '').trim();
            return content !== '' && content !== '"' && content !== '",';
          });
          
          // Join all valid numbered points with newlines
          fullCapsule = validPoints.join('\n\n');
          console.log(`Extracted ${validPoints.length} valid numbered points`);
        }
        
        // Remove any remaining JSON artifacts
        fullCapsule = fullCapsule
          .replace(/",\s*"keyInsights"[\s\S]*$/, '') // Remove everything after fullCapsule
          .replace(/\\n/g, '\n') // Replace escaped newlines
          .replace(/\\"/g, '"') // Replace escaped quotes
          .replace(/\\\\/g, '\\') // Replace escaped backslashes
          .replace(/\n\d+\.\s*\n/g, '\n') // Remove empty numbered points
          .replace(/\n{3,}/g, '\n\n'); // Replace multiple newlines with double newlines
      } catch (error) {
        console.error('Error cleaning fullCapsule content:', error);
      }
    } else {
      // If it's not a string, convert it to a string
      fullCapsule = JSON.stringify(capsule.content.fullCapsule);
    }
    
    // Remove any other fields that might be in the response
    // This ensures we only return the fullCapsule content
    return fullCapsule;
  } catch (error) {
    console.error('Error generating full capsule:', error);
    throw error;
  }
};

export const generateKeyInsights = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<string[]> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.keyInsights;
};

export const generateMindMap = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<MindMapNode> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.mindMap;
};

export const generateFAQs = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<FAQ[]> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.faqs;
};

export const generateMCQs = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<MCQ[]> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.mcqs;
};

export const generateFlashCards = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<FlashCard[]> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.flashCards;
};

export const generateStudyNotes = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<string> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.studyNotes;
};

export const generateTerminology = async (
  videoUrl: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<TermDefinition[]> => {
  const capsule = await createCapsule({
    videoUrl,
    outputLanguage,
    category
  });
  
  return capsule.content.terminology;
};

export const generateChatResponse = async (
  videoUrl: string,
  message: string,
  outputLanguage: string = OutputLanguage.English,
  category: string = ContentCategory.General
): Promise<string> => {
  // Extract video ID
  const videoId = extractVideoId(videoUrl);
  
  if (!videoId) {
    throw new Error('Invalid YouTube URL');
  }
  
  // Get video metadata
  const metadata = await getVideoMetadata(videoId);
  
  // Get video transcript
  const transcript = await getVideoTranscript(videoId);
  
  // Create chat message
  const messages: ChatMessage[] = [
    {
      role: 'user',
      content: message
    }
  ];
  
  // Chat with AI
  return await chatWithAI(messages, transcript, metadata);
};
